import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { FaCar, FaPlus, FaTrash, FaTimes, FaQrcode } from "react-icons/fa";

const D2Cars = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCarId, setEditingCarId] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedCarForQr, setSelectedCarForQr] = useState(null);
  const [imageMode, setImageMode] = useState("url"); // "url" or "upload"
  const [videoMode, setVideoMode] = useState("url"); // "url" or "upload"
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const handleMediaUpload = async (e, type = "image") => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (type === "video" && file.size > 100 * 1024 * 1024) {
      toast.error("Video file exceeds 100MB limit.");
      return;
    }
    if (type === "image" && file.size > 10 * 1024 * 1024) {
      toast.error("Image file exceeds 10MB limit.");
      return;
    }

    setUploadingMedia(true);
    const toastId = toast.loading(`Uploading ${type} to S3 bucket...`);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("media", file);

      const res = await fetch(`${getBackendBaseUrl()}/d2-cars/upload-media`, {
        method: "POST",
        body: uploadFormData
      });
      const data = await res.json();
      if (data.success && data.data?.url) {
        toast.update(toastId, {
          render: `✓ ${type === 'video' ? 'Video' : 'Image'} uploaded to S3 successfully!`,
          type: "success",
          isLoading: false,
          autoClose: 2500
        });
        if (type === "video") {
          setFormData(prev => ({ ...prev, videoUrl: data.data.url }));
        } else {
          setFormData(prev => ({ ...prev, imageUrl: data.data.url }));
        }
      } else {
        toast.update(toastId, {
          render: data.message || `Failed to upload ${type}`,
          type: "error",
          isLoading: false,
          autoClose: 3000
        });
      }
    } catch (err) {
      console.error(`Error uploading ${type} to S3:`, err);
      toast.update(toastId, {
        render: `Failed to upload ${type} to S3`,
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setUploadingMedia(false);
    }
  };
  const [formData, setFormData] = useState({
    stockNumber: "",
    vin: "",
    year: new Date().getFullYear(),
    make: "",
    model: "",
    trim: "",
    colors: [],
    mileage: 0,
    msrp: 0,
    salePrice: 0,
    imageUrl: "",
    videoUrl: "",
    docFee: 800,
    registration: 772
  });

  const handleOpenAddModal = () => {
    setEditingCarId(null);
    setFormData({
      stockNumber: `TY${Math.floor(10000 + Math.random() * 90000)}`,
      vin: `4T1B11HK${Math.floor(100000 + Math.random() * 900000)}`,
      year: new Date().getFullYear(),
      make: "Toyota",
      model: "",
      trim: "",
      colors: [{ name: "Midnight Black", hex: "#000000", imageUrl: "" }],
      mileage: 0,
      msrp: 35000,
      salePrice: 33500,
      imageUrl: "",
      videoUrl: "",
      docFee: 800,
      registration: 772
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (car) => {
    setEditingCarId(car._id);
    setFormData({
      stockNumber: car.stockNumber || "",
      vin: car.vin || "",
      year: car.year || new Date().getFullYear(),
      make: car.make || "",
      model: car.model || "",
      trim: car.trim || "",
      colors: car.colors?.length > 0 ? car.colors : (car.color ? [{ name: car.color, hex: "#000000", imageUrl: "" }] : []),
      mileage: car.mileage || 0,
      msrp: car.msrp || 0,
      salePrice: car.salePrice || 0,
      imageUrl: car.imageUrl || "",
      videoUrl: car.videoUrl || "",
      docFee: car.docFee !== undefined ? car.docFee : 800,
      registration: car.registration !== undefined ? car.registration : 772
    });
    setShowModal(true);
  };

  const handleFileChange = (e, index = null) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image file size exceeds 5MB limit.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (index !== null) {
          const newColors = [...formData.colors];
          newColors[index].imageUrl = reader.result;
          setFormData(prev => ({ ...prev, colors: newColors }));
        } else {
          setFormData(prev => ({ ...prev, imageUrl: reader.result }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddColor = () => {
    setFormData(prev => ({
      ...prev,
      colors: [...prev.colors, { name: "", hex: "#1e3a8a", imageUrl: "", imageMode: "url" }]
    }));
  };

  const handleColorChange = (index, field, value) => {
    const newColors = [...formData.colors];
    newColors[index][field] = value;
    setFormData(prev => ({ ...prev, colors: newColors }));
  };

  const handleRemoveColor = (index) => {
    const newColors = formData.colors.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, colors: newColors }));
  };

  const fetchCars = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-cars`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setCars(data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch cars", err);
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveCar = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("authToken");

      const colorsList = formData.colors.map(c => ({ name: c.name, hex: c.hex, imageUrl: c.imageUrl }));
      const singleColor = colorsList.length > 0 ? colorsList[0].name : "";

      const payload = {
        ...formData,
        color: singleColor,
        colors: colorsList
      };

      const url = editingCarId 
        ? `${getBackendBaseUrl()}/d2-cars/${editingCarId}`
        : `${getBackendBaseUrl()}/d2-cars`;
      
      const method = editingCarId ? "PUT" : "POST";

      const res = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(editingCarId ? "Car updated successfully!" : "Car added successfully!");
        setShowModal(false);
        setEditingCarId(null);
        fetchCars();
      } else {
        toast.error(data.message || "Failed to save car");
      }
    } catch (err) {
      console.error("Error saving car", err);
      toast.error("Failed to save car");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this car?")) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-cars/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Car deleted successfully");
        fetchCars();
      }
    } catch (err) {
      toast.error("Failed to delete car");
    }
  };

  return (
    <div className="p-8 h-full w-full bg-slate-50/50 flex flex-col overflow-y-auto animate-fade-in-up">
      {/* Title Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2.5 bg-blue-50 text-blue-600 rounded-2xl shadow-sm border border-blue-100 animate-pulse">
              <FaCar size={24} />
            </span>
            Car Master Data
          </h1>
          <p className="text-slate-500 text-xs mt-1 leading-relaxed">
            Manage your inventory database, configure vehicle attributes, media video links, and set pricing.
          </p>
        </div>
        <button
          onClick={handleOpenAddModal}
          className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 shadow-md transition-all duration-200 flex items-center gap-2 text-sm cursor-pointer"
        >
          <FaPlus size={12} /> Add New Car
        </button>
      </div>

      {/* Inventory Cards Grid */}
      {loading ? (
        <div className="flex h-64 items-center justify-center text-slate-450 text-sm">
          <div className="flex flex-col items-center justify-center gap-2">
            <span className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>
            Loading showroom inventory...
          </div>
        </div>
      ) : cars.length === 0 ? (
        <div className="bg-white border border-slate-200/60 rounded-3xl p-12 text-center text-slate-400 text-sm italic shadow-sm">
          No cars found in database. Click "+ Add New Car" to add your first vehicle.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cars.map((car) => {
            const displayImage = car.imageUrl || "https://images.unsplash.com/photo-1549399542-7e3f8b79c341?auto=format&fit=crop&q=80&w=400";
            return (
              <div key={car._id} className="bg-white rounded-3xl border border-slate-200/60 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                <div>
                  {/* Car Image Header */}
                  <div className="h-44 bg-slate-100 overflow-hidden relative border-b border-slate-100">
                    <img 
                      src={displayImage} 
                      alt={`${car.year} ${car.make} ${car.model}`}
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-4 left-4 bg-slate-900/80 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                      Stock: {car.stockNumber}
                    </span>
                    {car.videoUrl && (
                      <span className="absolute top-4 right-4 bg-cyan-600/90 text-white text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                        🎬 Video Live
                      </span>
                    )}
                  </div>

                  {/* Body Content */}
                  <div className="p-5 space-y-4">
                    <div>
                      <h3 className="text-base font-extrabold text-slate-800 leading-tight">
                        {car.year} {car.make} {car.model}
                      </h3>
                      <p className="text-slate-400 text-xs mt-0.5 font-medium">{car.trim || "Standard Trim"}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs border-t border-slate-100 pt-3 text-slate-650">
                      <div>
                        <span className="text-slate-400 block text-[9px] font-bold uppercase tracking-wider">Mileage</span>
                        <span className="font-semibold">{car.mileage?.toLocaleString() || 0} mi</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[9px] font-bold uppercase tracking-wider">Exterior Color</span>
                        <span className="font-semibold truncate block">{car.color || "N/A"}</span>
                      </div>
                      <div className="col-span-2 border-t border-slate-50 pt-2 flex justify-between items-baseline">
                        <div>
                          <span className="text-slate-400 block text-[9px] font-bold uppercase tracking-wider">MSRP</span>
                          <span className="text-slate-400 line-through font-semibold">${car.msrp?.toLocaleString()}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-slate-400 block text-[9px] font-bold uppercase tracking-wider">Sale Price</span>
                          <span className="text-emerald-600 font-extrabold text-base">${car.salePrice?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Controls with Edit Button */}
                <div className="p-5 border-t border-slate-100 bg-slate-50/50 flex items-center gap-2">
                  <button
                    onClick={() => handleOpenEditModal(car)}
                    className="bg-indigo-50 hover:bg-indigo-100 text-indigo-600 font-bold text-xs tracking-wide px-3.5 py-2.5 rounded-xl border border-indigo-100 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCarForQr(car);
                      setShowQrModal(true);
                    }}
                    className="flex-1 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs tracking-wide py-2.5 rounded-xl border border-slate-200 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <FaQrcode size={12} className="text-slate-500" /> Kiosk QR
                  </button>
                  <button
                    onClick={() => handleDelete(car._id)}
                    className="bg-red-50 hover:bg-red-100 text-red-600 font-bold text-xs tracking-wide px-3.5 py-2.5 rounded-xl border border-red-100 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add New Car Dialog Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative text-slate-800">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-655 p-1.5 hover:bg-slate-100 rounded-full transition-colors border-0 outline-none"
            >
              <FaTimes size={16} />
            </button>
            
            <h2 className="text-2xl font-bold text-slate-850 tracking-tight mb-2">
              {editingCarId ? "Edit Vehicle & Media" : "Add New Vehicle"}
            </h2>
            <p className="text-slate-400 text-xs mb-6 border-b pb-4">
              {editingCarId 
                ? "Update vehicle specifications, pricing, image files, and video showcase links."
                : "Enter vehicle specifications and pricing to save it to dealership inventory."}
            </p>
            
            <form onSubmit={handleSaveCar} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Stock Number *</label>
                <input required type="text" name="stockNumber" value={formData.stockNumber} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" placeholder="e.g. S10293" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">VIN *</label>
                <input required type="text" name="vin" value={formData.vin} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" placeholder="17-character VIN" />
              </div>
 
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Year *</label>
                <input required type="number" name="year" value={formData.year} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Make *</label>
                <input required type="text" name="make" value={formData.make} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" placeholder="e.g. Toyota" />
              </div>
 
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Model *</label>
                <input required type="text" name="model" value={formData.model} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" placeholder="e.g. Camry" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Trim</label>
                <input type="text" name="trim" value={formData.trim} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" placeholder="e.g. XSE V6" />
              </div>
 
              
              <div className="col-span-1 sm:col-span-2">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-xs font-bold text-slate-600 uppercase">Vehicle Colors</label>
                  <button type="button" onClick={handleAddColor} className="text-[10px] font-bold px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded border border-indigo-100 hover:bg-indigo-100 flex items-center gap-1">
                    <FaPlus size={8} /> Add Color
                  </button>
                </div>
                {formData.colors.map((colorItem, idx) => (
                  <div key={idx} className="p-3 border border-slate-200 rounded-xl mb-3 bg-slate-50/50 flex flex-col gap-3 relative">
                    <button type="button" onClick={() => handleRemoveColor(idx)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500">
                      <FaTimes size={12} />
                    </button>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 mb-1">Color Name</label>
                        <input type="text" value={colorItem.name} onChange={(e) => handleColorChange(idx, "name", e.target.value)} className="w-full border border-slate-200 rounded p-1.5 text-xs outline-none focus:border-indigo-400" placeholder="e.g. Midnight Black" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 mb-1">Hex Code</label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={colorItem.hex} onChange={(e) => handleColorChange(idx, "hex", e.target.value)} className="w-6 h-6 p-0 border-0 rounded cursor-pointer" />
                          <input type="text" value={colorItem.hex} onChange={(e) => handleColorChange(idx, "hex", e.target.value)} className="flex-1 border border-slate-200 rounded p-1.5 text-xs outline-none focus:border-indigo-400 uppercase font-mono" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-[10px] font-bold text-slate-500">Color Image (Optional)</label>
                        <div className="flex gap-1">
                          <button type="button" onClick={() => handleColorChange(idx, "imageMode", "url")} className={`text-[9px] px-1.5 py-0.5 rounded ${colorItem.imageMode === 'url' ? 'bg-slate-700 text-white' : 'bg-slate-200 text-slate-600'}`}>URL</button>
                          <button type="button" onClick={() => handleColorChange(idx, "imageMode", "upload")} className={`text-[9px] px-1.5 py-0.5 rounded ${colorItem.imageMode === 'upload' ? 'bg-slate-700 text-white' : 'bg-slate-200 text-slate-600'}`}>Upload</button>
                        </div>
                      </div>
                      {colorItem.imageMode === 'url' ? (
                        <input type="text" value={colorItem.imageUrl} onChange={(e) => handleColorChange(idx, "imageUrl", e.target.value)} className="w-full border border-slate-200 rounded p-1.5 text-xs outline-none focus:border-indigo-400" placeholder="https://..." />
                      ) : (
                        <div className="flex items-center gap-2">
                          <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, idx)} className="text-xs w-full" />
                          {colorItem.imageUrl && <img src={colorItem.imageUrl} alt="preview" className="w-8 h-8 rounded object-cover border" />}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Mileage *</label>
                <input required type="number" name="mileage" value={formData.mileage} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" />
              </div>
 
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">MSRP ($) *</label>
                <input required type="number" name="msrp" value={formData.msrp} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Sale Price ($) *</label>
                <input required type="number" name="salePrice" value={formData.salePrice} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" />
              </div>
 
              
              {/* VEHICLE PREVIEW VIDEO SOURCE (URL OR S3 UPLOAD) */}
              <div className="col-span-1 sm:col-span-2">
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-xs font-bold text-slate-600 uppercase">Vehicle Preview Video (Optional)</label>
                  <div className="flex gap-2">
                    <button 
                      type="button" 
                      onClick={() => setVideoMode("url")} 
                      className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                        videoMode === "url" 
                          ? "bg-slate-800 text-white border-slate-800 shadow-sm" 
                          : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      Use URL
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setVideoMode("upload")} 
                      className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                        videoMode === "upload" 
                          ? "bg-slate-800 text-white border-slate-800 shadow-sm" 
                          : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      Upload Video (S3)
                    </button>
                  </div>
                </div>

                {videoMode === "url" ? (
                  <input 
                    type="text" 
                    name="videoUrl" 
                    value={formData.videoUrl} 
                    onChange={handleInputChange} 
                    className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" 
                    placeholder="e.g. https://... or MP4/YouTube link" 
                  />
                ) : (
                  <div className="flex items-center gap-3">
                    <label className="flex-1 border-2 border-dashed border-slate-200 hover:border-indigo-500 hover:bg-slate-50/50 rounded-2xl p-4 flex flex-col items-center justify-center cursor-pointer transition-all">
                      <span className="text-xs text-indigo-600 font-bold">Choose Video File &rarr; Upload to S3</span>
                      <span className="text-[10px] text-slate-400 mt-1">MP4, MOV, WEBM, MKV (Max 100MB)</span>
                      <input 
                        type="file" 
                        accept="video/*" 
                        onChange={(e) => handleMediaUpload(e, "video")} 
                        className="hidden" 
                      />
                    </label>
                    {formData.videoUrl && (
                      <div className="w-24 h-16 rounded-xl border border-slate-200 overflow-hidden relative flex-shrink-0 shadow-sm bg-slate-950">
                        <video src={formData.videoUrl} className="w-full h-full object-cover" muted loop autoPlay />
                        <button 
                          type="button" 
                          onClick={() => setFormData(prev => ({ ...prev, videoUrl: "" }))}
                          className="absolute inset-0 bg-red-600/90 text-white font-bold text-[10px] uppercase flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity border-0 cursor-pointer"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {formData.videoUrl && videoMode === "upload" && (
                  <p className="text-[10px] text-emerald-600 font-bold mt-1 truncate">
                    ✓ Video uploaded to S3: {formData.videoUrl}
                  </p>
                )}
              </div>

              {/* VEHICLE IMAGE SOURCE (URL OR S3 UPLOAD) */}
              <div className="col-span-1 sm:col-span-2">
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-xs font-bold text-slate-600 uppercase">Vehicle Image Source</label>
                  <div className="flex gap-2">
                    <button 
                      type="button" 
                      onClick={() => setImageMode("url")} 
                      className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                        imageMode === "url" 
                          ? "bg-slate-800 text-white border-slate-800 shadow-sm" 
                          : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      Use URL
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setImageMode("upload")} 
                      className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                        imageMode === "upload" 
                          ? "bg-slate-800 text-white border-slate-800 shadow-sm" 
                          : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      Upload File (S3)
                    </button>
                  </div>
                </div>

                {imageMode === "url" ? (
                  <input 
                    type="text" 
                    name="imageUrl" 
                    value={formData.imageUrl} 
                    onChange={handleInputChange} 
                    className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" 
                    placeholder="e.g. https://images.unsplash.com/photo-..." 
                  />
                ) : (
                  <div className="flex items-center gap-3">
                    <label className="flex-1 border-2 border-dashed border-slate-200 hover:border-indigo-500 hover:bg-slate-50/50 rounded-2xl p-4 flex flex-col items-center justify-center cursor-pointer transition-all">
                      <span className="text-xs text-indigo-600 font-bold">Choose Image File &rarr; Upload to S3</span>
                      <span className="text-[10px] text-slate-400 mt-1">PNG, JPG, WEBP (Max 10MB)</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => handleMediaUpload(e, "image")} 
                        className="hidden" 
                      />
                    </label>
                    {formData.imageUrl && (
                      <div className="w-16 h-16 rounded-xl border border-slate-200 overflow-hidden relative flex-shrink-0 shadow-sm">
                        <img src={formData.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                        <button 
                          type="button" 
                          onClick={() => setFormData(prev => ({ ...prev, imageUrl: "" }))}
                          className="absolute inset-0 bg-red-600/90 text-white font-bold text-[10px] uppercase flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity border-0 cursor-pointer"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {formData.imageUrl && imageMode === "upload" && (
                  <p className="text-[10px] text-emerald-600 font-bold mt-1 truncate">
                    ✓ Image uploaded to S3: {formData.imageUrl}
                  </p>
                )}
              </div>
 
              <div className="col-span-1 sm:col-span-2 flex justify-end gap-3 mt-6 border-t pt-5">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 border border-slate-200 rounded-xl text-slate-650 hover:bg-slate-50 hover:text-slate-800 font-semibold text-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl text-white font-semibold hover:shadow-lg shadow-md transition-all text-sm"
                >
                  Save Vehicle
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Showroom QR Code Modal */}
      {showQrModal && selectedCarForQr && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in-up">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-8 w-full max-w-sm overflow-hidden relative text-center text-slate-800">
            <button 
              onClick={() => setShowQrModal(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-100 rounded-full transition-colors border-0 outline-none"
            >
              <FaTimes size={16} />
            </button>
            
            <h2 className="text-xl font-bold text-slate-800 tracking-tight mb-1">Showroom QR Code</h2>
            <p className="text-slate-400 text-xs mb-6">
              {selectedCarForQr.year} {selectedCarForQr.make} {selectedCarForQr.model}
            </p>
            
            <div className="bg-slate-50 border border-slate-100 p-6 rounded-2xl inline-block shadow-inner mb-6">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${window.location.origin}/kiosk?carId=${selectedCarForQr._id}`)}`}
                alt="Showroom QR Code"
                className="w-48 h-48 mx-auto"
              />
            </div>
            
            <div className="text-slate-500 text-[11px] leading-relaxed mb-4">
              Print and place this QR code on the car window. Customers can scan it to launch the interactive kiosk guided deal flow on their mobile.
            </div>
            
            <button
              onClick={() => window.print()}
              className="w-full bg-slate-800 hover:bg-slate-900 text-white font-semibold py-2.5 rounded-xl text-sm transition-all"
            >
              Print QR Code
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default D2Cars;
